import React, { useMemo, Fragment, useContext, useCallback } from 'react';
import { Skeleton } from '@material-ui/lab';
import { Tooltip, Collapse, IconButton, Checkbox } from '@material-ui/core';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from '@material-ui/icons';
import moment from 'moment';
import Input from '../../subComponents/input';
import DateWrapper from '../../subComponents/date-wrapper';
import Select from '../../subComponents/select';
import Modal from '../../subComponents/commonModal/v3';
import PPButton from '../../subComponents/button';
import { POPUP_TYPE } from '../../subComponents/popup';
import PatientChartContext from '../../../contexts/patientChart';
import { FEATURE } from '../../../utils/feature';
import { SALESFORCE_NOT_CONFIGURED_MESSAGE } from '../../../constants/emails.constant';
import {
  EXPAND_MORE_ICON_CLASS, RESPONSE_POPUP_KEY,
  MODAL_KEY, DIMENSION, 
} from '../../../constants/patientChart.constant';
import { TASK_STATUS_TYPE, TASK_TYPES, TASK_TYPES_COLLECTION } from '../../../constants/tasks.constant';

const hasFeature = FEATURE();

const DateFormat = 'YYYY-MM-DD';
const MaxDate = moment().format(DateFormat);

const TasksHeading = () => (
  <div
    className={`
      flex align-items-center
      justify-content-flex-start
      height-38
      p-l-3
      p-r-3
      bg-lighter-grey
      sticky
    `}
  >
    <div className='flex flex-fill align-items-center'>
      <div className='min-width-30'></div>
      <div className='min-width-160 max-width-160 font-roboto font-14 m-r-2'>Type</div>
      <div className='min-width-130 max-width-130 font-roboto font-14 m-r-2'>SubType</div>
      <div className='min-width-130 max-width-130 font-roboto font-14 m-r-2'>Status</div>
      <div className='font-roboto font-14'>Created Date</div>
    </div>
  </div>
);

const Loading = () => (
  <div className='flex flex-column flex-fill'>
    <TasksHeading />
    <div className={`
      flex
      align-items-center
      justify-content-space-between
      height-38
      p-l-3
      p-r-3
    `}>
      <div className='flex flex-fill align-items-center'>
        <div className='min-width-30'></div>
        <Skeleton variant='text' className='width-160 m-r-2' />
        <Skeleton variant='text' className='width-130 m-r-2' />
        <Skeleton variant='text' className='width-130 m-r-2' />
        <div className='align-items-center justify-content-space-between'>
          <Skeleton variant='text' className='width-120' />
        </div>
      </div>
    </div>
  </div>
);

const NoTasks = () => (
  <div className='flex flex-column flex-fill'>
    <TasksHeading />
    <div
      className={`
        flex
        flex-column
        align-items-flex-start
        justify-content-center
        in-progress
        p-l-3
        p-r-3
      `}
    >
      <div className='flex align-items-center height-40 font-roboto font-14'>No task for this patient.</div>

      {
        !hasFeature.IS_SALESFORCE_CONFIGURED && (
          <div className='flex align-items-center height-40 font-roboto font-14 color-red-v1'>{SALESFORCE_NOT_CONFIGURED_MESSAGE}</div>
        )
      }
    </div>
  </div>
);

const EditModalTemplate = () => {
  const { taskStatus, setTaskStatus } = useContext(PatientChartContext);

  const handleOrderNoteChange = useCallback((event) => {
    event.preventDefault();
    setTaskStatus({ ...taskStatus, orderNote: event.target.value });
  }, [taskStatus]);

  const handleSendTextToPatientChange = useCallback((_, sendTextToPatient = false) => {
    setTaskStatus({ ...taskStatus, sendTextToPatient });
  }, [taskStatus]);
 
  return (
    <div className='flex flex-column flex-fill gap-16 m-b-5'>
      <div className='flex flex-column flex-fill gap-8'>
        <div className='flex align-items-center gap-4'>
          <label className="bold font-14 tracking-wide">Order Note:</label>
          <span className='required font-24'>*</span>
        </div>
        <textarea
          name='orderNote'
          className='flex flex-fill border-light-grey-1 min-height-160 height-full p-4 font-proxima-nova font-16 color-dark-grey border-box resize-none'
          wrap='soft'
          maxLength={4000}
          value={taskStatus.orderNote}
          onChange={handleOrderNoteChange}
        />
      </div>
  
      <div className='flex align-items-center gap-8'>
        <label className="input-label tracking-wide">Also add to Patient Note:</label>
        <Checkbox
          title="sendTextToPatient"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={taskStatus.sendTextToPatient}
          onChange={handleSendTextToPatientChange}
        />
      </div>
    </div>
  );
};

/**
 * @param {{
 *    action: {
 *      getTasks: () => Promise<{}>,
 *      getTasksByOId: () => Promise<{}>,
 *      updateTask: () => Promise<{}>,
 *      getNotesTabInfo: () => void,
 *    };
 * }} props
 * @returns {React.JSX.Element} 
 */
const EditModalActions = (props) => {
  const {
    loading,
    setLoading,
    taskStatus,
    handleModalOpen,
    handleMultipleModalClose,
  } = useContext(PatientChartContext);
  const { action: { getTasks, getTasksByOId, getNotesTabInfo, updateTask } } = props;

  const handleUpdateTaskStatus = useCallback(async ({
    key = '',
  }) => {
    const {
      id: recordId,
      orderId,
      patientId,
      orderNote,
      status: _status,
      sendTextToPatient,
    } = taskStatus;

    const requestBody = {
      recordId,
      orderId,
      patientId,
      taskStatus: _status,
      orderNote,
      sendTextToPatient,
    };

    const _loading = new Set(loading);
    _loading.add(MODAL_KEY.CHANGE_TASK_STATUS);
    setLoading(_loading);

    try {
      const { status, data } = await updateTask(requestBody);
      if (status === 200) {
        handleMultipleModalClose.call(this, { keys: [key] });

        // reload the tasks & notes
        getTasks();
        getTasksByOId({ OrderId: orderId });
        getNotesTabInfo();
      }

      if (status !== 200) {
        const { Error: error = 'Failed to change task status. Please try later.' } = data;
        const heading = 'Error';

        handleModalOpen({
          key: RESPONSE_POPUP_KEY.CHANGE_TASK_STATUS,
          modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message: error,
          }
        });
      }
    } catch (e) {
      // error handling when needed
    } finally {
      const __loading = new Set(_loading);
      __loading.delete(MODAL_KEY.CHANGE_TASK_STATUS);
      setLoading(__loading);
    }
  }, [loading, taskStatus, handleModalOpen, handleMultipleModalClose]);

  const _loading = useMemo(() => loading.has(MODAL_KEY.CHANGE_TASK_STATUS), [loading]);

  const _disabled = useMemo(() => {
    if (_loading) {
      return _loading;
    }

    return taskStatus.orderNote.length < 3;
  }, [_loading, taskStatus]);

  return (
    <div className='flex justify-content-flex-end'>
      <PPButton
        label='Update Status'
        variant='contained'
        loading={_loading}
        disabled={_disabled}
        endGutter
        onClick={handleUpdateTaskStatus.bind(this, { key: MODAL_KEY.CHANGE_TASK_STATUS, modal: {} })}
      />
      <PPButton
        label='Cancel'
        variant='cancel'
        disabled={_loading}
        onClick={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.CHANGE_TASK_STATUS] })}
      />
    </div>
  );
};

/**
 * @param {{
 *    action: {
 *      getTasks: () => Promise<{}>,
 *      updateTask: () => Promise<{}>,
 *      getNotesTabInfo: () => void,
 *    };
 * }} props
 * @returns {React.JSX.Element} 
 */
const EditModal = (props) => {
  const {
    ModalCollection,
    handleMultipleModalClose,
  } = useContext(PatientChartContext);

  const popupConfig = useMemo(() => {
    const popup = {
      heading: 'Message',
      type: POPUP_TYPE.INFO,
      message: 'API response appears here...',
    };

    if (!ModalCollection.has(RESPONSE_POPUP_KEY.CHANGE_TASK_STATUS)) {
      return popup;
    }

    return ModalCollection.get(RESPONSE_POPUP_KEY.CHANGE_TASK_STATUS) || popup;
  }, [ModalCollection]);

  return (
    <Modal
      title={'Task Status Update'}
      open={ModalCollection.has(MODAL_KEY.CHANGE_TASK_STATUS)}
      style={DIMENSION.SMALL}
      responsePopup={ModalCollection.has(RESPONSE_POPUP_KEY.CHANGE_TASK_STATUS)}
      responsePopupType={popupConfig.type}
      responsePopupHeading={popupConfig.heading}
      responsePopupMessage={popupConfig.message}
      onResponsePopupOk={handleMultipleModalClose.bind(this, { keys: [RESPONSE_POPUP_KEY.CHANGE_TASK_STATUS] })}
    >
      <div className='flex flex-column flex-fill m-5 patient-chart-container'>
        <EditModalTemplate />
        <EditModalActions { ...props } />
      </div>
    </Modal>
  );
}

/**
 * @param {{
 *    Id: string;
 *    OMSOrderId: string;
 *    TaskType: string;
 *    TaskSubtype: string;
 *    TaskStatus: string;
 *    CreatedDate: string;
 *    OrderStatus: string;
 *    NoteToHCP: string;
 *    UpdateToTask: string;
 *    borderClass: string;
 * }} props 
 * @returns {React.JSX.Element}
 */
const AccordionContent = ({
  borderClass,
  ...props
}) => {
  const { Id, TaskStatus, TaskType, TaskSubtype, NoteToHCP, UpdateToTask, CreatedDate } = props;
  const { orderId, patientId, taskStatus, setTaskStatus, handleModalOpen } = useContext(PatientChartContext);

  const createdAt = useMemo(() => moment(CreatedDate).format(DateFormat), [CreatedDate]);

  const isCompleted = useMemo(() => TaskStatus === TASK_STATUS_TYPE.Complete, [TaskStatus]);

  const handleChange = (name, { target }) => {
    setTaskStatus({
      ...taskStatus,
      id: Id,
      status: target.value,
      orderId,
      patientId,
      orderNote: '',
      sendTextToPatient: false,
    });

    switch (name) {
      case 'status': {
        const hasTask = TASK_TYPES_COLLECTION.has(target.value);
        if (hasTask) {
          handleModalOpen({ key: MODAL_KEY.CHANGE_TASK_STATUS, modal: {} });
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  return (
    <div
      className={`
        flex
        flex-column
        flex-fill
        gap-16
        p-l-6
        p-r-3
        p-t-3
        p-b-3
        ${borderClass}
      `}
    >
      <div className='flex gap-16'>
        <div className='flex flex-column gap-8'>
          <label className="font-14 tracking-wide">Task Create Date:</label>
          <DateWrapper
            name='createdAt'
            label=''
            value={createdAt}
            max={MaxDate}
            type='date'
            readOnly
            inputClassName='min-width-280 cursor-not-allowed'
            onChange={handleChange.bind(this, 'createdAt')}
          />
        </div>

        <div className='flex flex-column flex-fill gap-8'>
          <label htmlFor='status' className="font-14 tracking-wide">Task Status:</label>
          <Select
            id='status'
            labelId="status"
            value={TaskStatus}
            readOnly={isCompleted}
            disabled={isCompleted}
            className='flex'
            onChange={handleChange.bind(this, 'status')}
            items={TASK_TYPES}
          />
        </div>
      </div>
        
      <div className='flex flex-column gap-8'>
        <label className="font-14 tracking-wide">Task Type:</label>
        <Input
          name='type'
          label=''
          className='flex cursor-not-allowed'
          readOnly
          value={TaskType}
          onChange={handleChange.bind(this, 'type')}
        />
      </div>

      <div className='flex flex-column gap-8'>
        <label className="font-14 tracking-wide">Task SubType:</label>
        <Input
          name='subType'
          label=''
          className='flex flex-fill cursor-not-allowed'
          readOnly
          value={TaskSubtype}
          onChange={handleChange.bind(this, 'subType')}
        />
      </div>

      <div className='flex flex-column gap-8'>
        <label className="font-14 tracking-wide">Note to HCP:</label>
        <Input
          name='noteToHCP'
          label=''
          className='flex flex-fill cursor-not-allowed'
          readOnly
          value={NoteToHCP}
          onChange={handleChange.bind(this, 'noteToHCP')}
        />
      </div>

      <div className='flex flex-column gap-8'>
        <label className="font-14 tracking-wide">Update to Task:</label>
        <Input
          name='updateToTask'
          label=''
          className='flex flex-fill cursor-not-allowed'
          readOnly
          value={UpdateToTask}
          onChange={handleChange.bind(this, 'updateToTask')}
        />
      </div>

      <div className='flex flex-column gap-8 d-none'>
        <label className="font-14 tracking-wide">HCP Note:</label>
        <Input
          name='orderNote'
          label=''
          className='flex flex-fill'
          readOnly
          value={''}
          onChange={handleChange.bind(this, 'orderNote')}
        />
      </div>
    </div>
  );
};

/**
 * @param {{
 *    Id: string;
 *    borderClass: string;
 *    backgroundClass: string;
 *    TaskType: string;
 *    TaskSubtype: string;
 *    TaskStatus: string;
 *    CreatedDate: string;
 *    formattedCreatedAt: string;
 *    OrderStatus: string;
 * }} props 
 * @returns {React.JSX.Element}
 */
const TaskAccordion = (props) => {
  const {
    Id,
    TaskType,
    TaskSubtype,
    TaskStatus,
    CreatedDate,
    formattedCreatedAt,
    backgroundClass,
  } = props;

  const { collapsible, handleExpandMultiple, handleCollapseMultiple } = useContext(PatientChartContext);

  const handleToggle = ({ key }) => {
    const request = { keys: [key] };
    if (!collapsible.has(key)) {
      handleExpandMultiple(request);
    } else {
      handleCollapseMultiple(request);
    }
  };

  return (
    <Fragment>
      <div className={`
        flex
        align-items-center
        justify-content-flex-start
        height-38
        p-l-3
        p-r-3
        m-t-2
        ${backgroundClass}
      `}>
        <div className='flex flex-fill align-items-center'>
          <div className='min-width-30'>
            <IconButton size='small' onClick={handleToggle.bind(this, { key: Id })}>
              <ExpandMoreRoundedIcon fontSize='small' className={EXPAND_MORE_ICON_CLASS.get(collapsible.has(Id))} />
            </IconButton>
          </div>
          
          <Tooltip title={TaskType} placement='top' arrow>
            <div className='min-width-160 max-width-160 font-roboto font-14 m-r-2 overflow-ellipsis'>{TaskType}</div>
          </Tooltip>
  
          <Tooltip title={TaskSubtype} placement='top' arrow>
            <div className='min-width-130 max-width-130 font-roboto font-14 m-r-2 overflow-ellipsis'>{TaskSubtype}</div>
          </Tooltip>
  
          <Tooltip title={TaskStatus} placement='top' arrow>
            <div className='min-width-130 max-width-130 font-roboto font-14 m-r-2 overflow-ellipsis'>{TaskStatus}</div>
          </Tooltip>

          <Tooltip title={CreatedDate} placement='top' arrow>
            <div className='font-roboto font-14 overflow-ellipsis'>{formattedCreatedAt}</div>
          </Tooltip>
        </div>
      </div>

      <Collapse in={collapsible.has(Id)} timeout='auto' unmountOnExit>
        <AccordionContent { ...props } />
      </Collapse>
    </Fragment>
  );
};

/**
 * @param {{
 *    Id: string;
 *    backgroundClass: (index: number) => string;
 *    borderClass: (index: number) => string;
 * }} props 
 * @returns {React.JSX.Element}
 */
const AccordionContentWithoutClasses = ({ Id, backgroundClass, borderClass, ...props }) => (
  <AccordionContent
    key={Id}
    Id={Id}
    backgroundClass={backgroundClass(0)}
    borderClass={borderClass(0)}
    {...props}
  />
);

/**
 * @param {{
 *    tasks: {
 *      Id: string;
 *    }[];
 *    backgroundClass: (index: number) => string;
 *    borderClass: (index: number) => string;
 * }} props 
 * @returns {React.JSX.Element}
 */
const TaskAccordionsWithoutClasses = ({ tasks, backgroundClass, borderClass }) => (
  <Fragment>
    <TasksHeading />
    {
      tasks.map(({ Id, ...props }, index) => (
        <TaskAccordion
          key={Id}
          Id={Id}
          backgroundClass={backgroundClass(index)}
          borderClass={borderClass(index)}
          {...props}
        />
      ))
    }
  </Fragment>
);

/**
 * 
 * @param {{
 *    orderId: string;
 *    tasks: {
 *      Id: string,
 *    }[];
 *    action: {
 *      getTasks: () => Promise<{}>,
 *      updateTask: () => Promise<{}>,
 *      getNotesTabInfo: () => void,
 *    };
 * }} props 
 * @returns {React.JSX.Element} 
 */
const TasksAccordionWrapper = ({
  tasks,
  action,
}) => {
  const backgroundClass = useCallback((index = 0) => {
    let className = 'bg-lighter-grey';
    if (index % 2 === 0) {
      className = 'bg-lightest-primary';
    }
    return className;
  }, []);

  const borderClass = useCallback((index = 0) => {
    let className = 'border-2-lighter-grey';
    if (index % 2 === 0) {
      className = 'border-2-lightest-primary';
    }
    return className;
  }, []);

  const { isLastTask, lastTask } = useMemo(() => {
    if (tasks.length === 1) {
      return {
        isLastTask: true,
        lastTask: tasks[0],
      };
    }
    return {
      isLastTask: false,
      lastTask: {},
    };
  }, [tasks]);

  const TaskAccordionCollection = new Map([
    [true, AccordionContentWithoutClasses],
    [false, TaskAccordionsWithoutClasses]
  ]);

  const TaskAccordionPropsCollection = new Map([
    [true, { ...lastTask, backgroundClass, borderClass }],
    [false, { tasks, backgroundClass, borderClass }],
  ]);

  const AccordionUI = TaskAccordionCollection.get(isLastTask);
  const auiProps = TaskAccordionPropsCollection.get(isLastTask);

  return (
    <div className='flex flex-column flex-fill'>
      <EditModal action={action} />
      <AccordionUI { ...auiProps } />
    </div>
  );
}

const UIComponentCollection = new Map([
  ['true-false-true', TasksAccordionWrapper],
  ['true-false-false', NoTasks],

  ['false-false-false', Loading],
  ['true-true-false', Loading],
  ['true-true-true', Loading],
]);

/**
 * @param {{
 *    tabKey: string;
 *    selectedKey: string;
 *    data: {
 *      orderId: string,
 *      tasksInfo: {
 *        called: boolean,
 *        loading: boolean,
 *        data: {
 *          Id: string,
 *          OMSOrderId: string,
 *        }[],
 *      },
 *    };
 *    action: {
 *      getTasks: () => Promise<{}>,
 *      updateTask: () => Promise<{}>,
 *      getNotesTabInfo: () => void,
 *    };
 * }} props 
 * @returns {React.JSX.Element}
 */
export const Tasks = ({
  tabKey = '',
  selectedKey = '',
  data: {
    orderId,
    tasksInfo
  },
  action,
}) => {
  const displayClass = useMemo(() => tabKey !== selectedKey ? 'd-none' : '', [tabKey, selectedKey]);
  const tasks = useMemo(() => {
    if (!tasksInfo?.data?.length) {
      return [];
    }
    return tasksInfo.data.filter((task) => task?.OMSOrderId === orderId);
  }, [orderId, tasksInfo.data]);
  const stateKey = useMemo(() => `${tasksInfo.called}-${tasksInfo.loading}-${Boolean(tasks.length)}`, [tasksInfo, tasks]);

  const UIElement = UIComponentCollection.get(stateKey);
  const UIComponentPropsCollection = new Map([
    ['true-false-true', {
      orderId,
      tasks,
      action,
    }],
    ['true-false-false', {}],

    ['false-false-false', {}],
    ['true-true-false', {}],
    ['true-true-true', {}],
  ]);
  const elementProps = UIComponentPropsCollection.get(stateKey);

  return (
    <div className={`flex flex-column flex-fill column-content scroll-y-auto ${displayClass}`}>
      <UIElement { ...elementProps } />
    </div>
  );
}

export default Tasks;
